import { useEffect, useRef, useState } from 'react';
import { LOGO_360, LOGO_CASINO, LOGO_VIEWBOX, LOGO_W } from '../assets/w360-logo-paths';

/**
 * The W360 CASINO wordmark as inline SVG so each part can move on its own:
 * - `header`: lobby top bar — "360" flips like a coin every few seconds, CASINO letters ripple gold and a shine sweeps.
 * - `intro`: the big reveal on the game loading screen — W slams in, 360 spins in, CASINO drops letter by letter.
 */
export function W360Logo({ variant = 'header', className = '' }: { variant?: 'header' | 'intro'; className?: string }) {
  const id = variant === 'intro' ? 'w360i' : 'w360h';
  return (
    <svg className={`w360-logo ${variant} ${className}`} viewBox={LOGO_VIEWBOX} role="img" aria-label="W360 Casino">
      <title>W360 Casino</title>
      <defs>
        <linearGradient id={`${id}-gold`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff6c9" />
          <stop offset="0.45" stopColor="#ffd34d" />
          <stop offset="0.55" stopColor="#f0a81c" />
          <stop offset="1" stopColor="#b86b00" />
        </linearGradient>
        <linearGradient id={`${id}-orange`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffb04a" />
          <stop offset="0.5" stopColor="#ff6a00" />
          <stop offset="1" stopColor="#d94800" />
        </linearGradient>
        <linearGradient id={`${id}-shine`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.95" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <path d={LOGO_W} />
          <path d={LOGO_360} />
          {LOGO_CASINO.flat().map(([d], i) => (
            <path key={i} d={d} />
          ))}
        </clipPath>
      </defs>
      <g className="lg-w">
        <path d={LOGO_W} fill="#fff" />
      </g>
      <g className="lg-360">
        <path d={LOGO_360} fill={`url(#${id}-orange)`} />
      </g>
      <g className="lg-casino">
        {LOGO_CASINO.map((parts, i) => (
          <g key={i} className="lg-l" style={{ ['--i' as string]: i }} fill={variant === 'intro' ? `url(#${id}-gold)` : '#E5E7EB'}>
            {parts.map(([d, rule], k) => (
              <path key={k} d={d} fillRule={rule} />
            ))}
          </g>
        ))}
      </g>
      <g clipPath={`url(#${id}-clip)`} className="lg-shine-g">
        <rect className="lg-shine" x="-260" y="-20" width="200" height="170" fill={`url(#${id}-shine)`} transform="skewX(-20)" />
      </g>
    </svg>
  );
}

/** A short synthesized sting (whoosh + bright chord) — only plays if the browser already allows audio (e.g. the player clicked into the game). */
function playSting(): void {
  try {
    const Ctor: typeof AudioContext | undefined = (globalThis as any).AudioContext ?? (globalThis as any).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    if (ctx.state !== 'running') {
      void ctx.close();
      return;
    }
    const t0 = ctx.currentTime + 0.05;
    const out = ctx.createGain();
    out.gain.value = 0.35;
    out.connect(ctx.destination);
    // whoosh: filtered noise sweep
    const len = ctx.sampleRate * 0.9;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) ch[i] = (Math.random() * 2 - 1) * Math.sin((Math.PI * i) / len);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.2;
    bp.frequency.setValueAtTime(300, t0);
    bp.frequency.exponentialRampToValueAtTime(4000, t0 + 0.8);
    const ng = ctx.createGain();
    ng.gain.value = 0.6;
    noise.connect(bp).connect(ng).connect(out);
    noise.start(t0);
    // impact + chord as the letters land
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, k) => {
      const o = ctx.createOscillator();
      o.type = k === 0 ? 'triangle' : 'sine';
      o.frequency.value = f;
      const g = ctx.createGain();
      const s = t0 + 0.75 + k * 0.09;
      g.gain.setValueAtTime(0, s);
      g.gain.linearRampToValueAtTime(0.22, s + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, s + 1.6);
      o.connect(g).connect(out);
      o.start(s);
      o.stop(s + 1.7);
    });
    const boom = ctx.createOscillator();
    boom.frequency.setValueAtTime(140, t0 + 0.7);
    boom.frequency.exponentialRampToValueAtTime(40, t0 + 1.2);
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.7, t0 + 0.7);
    bg.gain.exponentialRampToValueAtTime(0.001, t0 + 1.3);
    boom.connect(bg).connect(out);
    boom.start(t0 + 0.7);
    boom.stop(t0 + 1.35);
    setTimeout(() => void ctx.close().catch(() => undefined), 3500);
  } catch {
    /* audio is optional */
  }
}

/**
 * Full-screen W360 CASINO introduction shown at the start of every game load. It holds for at least `minMs`
 * and then fades into the game's own splash (or the game when it is ready). Tap to skip.
 */
export function BrandIntro({ minMs = 3200, reduced = false, tag = '', onDone }: { minMs?: number; reduced?: boolean; tag?: string; onDone: () => void }) {
  const [leaving, setLeaving] = useState(false);
  const done = useRef(false);
  const finish = () => {
    if (done.current) return;
    done.current = true;
    setLeaving(true);
    setTimeout(onDone, 450);
  };
  useEffect(() => {
    if (!reduced) playSting();
    const t = setTimeout(finish, reduced ? 900 : minMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={`brand-intro${leaving ? ' leaving' : ''}${reduced ? ' reduced' : ''}`} data-testid="brand-intro" onClick={finish} role="presentation">
      <div className="bi-rays" />
      <div className="bi-glow" />
      <div className="bi-sparks">
        {Array.from({ length: 24 }, (_, i) => (
          <i key={i} style={{ ['--a' as string]: `${(i * 360) / 24}deg`, ['--d' as string]: `${(i % 5) * 0.06}s`, ['--r' as string]: `${38 + (i % 4) * 9}vmin` }} />
        ))}
      </div>
      <div className="bi-stage">
        <W360Logo variant="intro" />
        <div className="bi-tag">{tag}</div>
        <div className="bi-bar">
          <div />
        </div>
      </div>
    </div>
  );
}
